/**
 * LocationAnalytics Page
 *
 * Admin page for viewing modem analytics and network availability
 * per location. Refactored from the old Modems page.
 *
 * Features:
 * - Location selector (hierarchical)
 * - Multi-modem selector (filtered by location)
 * - Aggregated stats for selected modems
 * - Signal strength visualization
 * - Network availability at location
 * - Aggregated capabilities at location
 */

import { useState, useEffect, useMemo } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Spinner, MultiSelectFilter } from '@/components/ui';
import type { MultiSelectOption } from '@/components/ui';
import {
  BarChart3,
  Signal,
  Power,
  Activity,
  Network,
  Radio,
  RefreshCw,
  MapPin,
  Cpu,
  Wifi,
  CheckCircle2,
  AlertCircle,
  Clock,
  Filter,
} from 'lucide-react';
import { BarChart } from '@/components/charts/BarChart';
import { GaugeChart } from '@/components/charts/GaugeChart';
import { useAuth } from '@/contexts/AuthContext';
import { useDeviceVisibility, filterDevicesByTier } from '@/hooks/useDeviceVisibility';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import { networkAvailabilityService } from '@/services/networkAvailabilityService';
import { getRegions, getCountries, getLocations, filterLocationsByTenancy } from '@/services/hierarchicalLocationService';
import type { CentralEdgeDevice, CentralEdgeModem, Location, Region, Country, AggregatedCapabilities } from '@simcheck/shared-types';
import { cn, formatRelativeTime } from '@/lib/utils';
import { useToast } from '@/contexts/ToastContext';

interface ModemStats {
  totalModems: number;
  onlineModems: number;
  offlineModems: number;
  avgSignal: number;
  signalDistribution: { range: string; count: number }[];
}

// Network info aggregated from modem scans
interface AggregatedNetwork {
  id: string;
  networkName: string;
  mcc: string;
  mnc: string;
  networkType?: string;
  networkGeneration?: string;
  modemCount: number;
  isAvailable: boolean;
  modemIds: string[];  // Track which modems detected this network
  latestTimestamp?: Date;  // Most recent scan timestamp
  isStale: boolean;  // True if scan is older than staleness threshold
}

// Staleness threshold in milliseconds (4 hours)
const STALENESS_THRESHOLD_MS = 4 * 60 * 60 * 1000;

export function LocationAnalytics() {
  const { activeOrganization, isSystemAdmin, isSuperAdmin } = useAuth();
  const visibilityFlags = useDeviceVisibility();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { success, error: showError } = useToast();

  // Multi-select filter state
  const [regions, setRegions] = useState<Region[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedRegionIds, setSelectedRegionIds] = useState<string[]>([]);
  const [selectedCountryIds, setSelectedCountryIds] = useState<string[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);

  // Modem selection state
  const [selectedModemIds, setSelectedModemIds] = useState<string[]>([]);
  const [allDevices, setAllDevices] = useState<CentralEdgeDevice[]>([]);
  const [allModems, setAllModems] = useState<CentralEdgeModem[]>([]);

  // Data state
  const [locationCapabilities, setLocationCapabilities] = useState<AggregatedCapabilities | null>(null);
  const [networksAtLocation, setNetworksAtLocation] = useState<AggregatedNetwork[]>([]);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingCapabilities, setLoadingCapabilities] = useState(false);

  // Check admin access
  useEffect(() => {
    if (!isSystemAdmin) {
      navigate('/');
    }
  }, [isSystemAdmin, navigate]);

  // Load filter options (regions, countries, locations)
  useEffect(() => {
    loadFilterOptions();
  }, [activeOrganization]);

  // Handle URL parameters for pre-filtering (Phase 7.5)
  useEffect(() => {
    const locationsParam = searchParams.get('locations');
    const regionsParam = searchParams.get('regions');
    const countriesParam = searchParams.get('countries');
    const networkParam = searchParams.get('network');

    if (locationsParam) {
      setSelectedLocationIds(locationsParam.split(',').filter(Boolean));
    }
    if (regionsParam) {
      setSelectedRegionIds(regionsParam.split(',').filter(Boolean));
    }
    if (countriesParam) {
      setSelectedCountryIds(countriesParam.split(',').filter(Boolean));
    }
    // Network filter is handled separately (used for network-to-location navigation)
  }, [searchParams]);

  // Load devices when filters change
  // IMPORTANT: Must wait for hierarchical data to be loaded first
  useEffect(() => {
    // Don't run until hierarchical data is loaded
    if (loading) {
      return;
    }

    const hasFilters = selectedRegionIds.length > 0 || selectedCountryIds.length > 0 || selectedLocationIds.length > 0;

    if (hasFilters) {
      loadFilteredDevices();
    } else {
      // No filters - load all devices
      loadAllDevices();
    }
  }, [selectedRegionIds, selectedCountryIds, selectedLocationIds, loading, locations, countries, regions]);

  const loadFilterOptions = async () => {
    try {
      setLoading(true);
      // Load all filter options in parallel
      const [regionsData, countriesData, locationsData] = await Promise.all([
        getRegions(),
        getCountries(),
        getLocations()
      ]);
      setRegions(regionsData);
      setCountries(countriesData);
      // Tenancy (#202): never render another org's private sites, and hard-scope
      // synthetic demo sites to their owning org (above the super-admin bypass)
      // so the filter chips aren't padded with 33 hardware-less "… Lab" ghosts.
      setLocations(
        filterLocationsByTenancy(locationsData, activeOrganization?.id, isSuperAdmin),
      );
    } catch (err) {
      console.error('Failed to load filter options:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllDevices = async () => {
    try {
      setLoadingDevices(true);
      // `filterDevicesByTier` also hard-scopes synthetic demo devices to their
      // owning org — without it the analytics counted 47 hardware-less units.
      const devices = filterDevicesByTier(await edgeDeviceService.getDevices(), visibilityFlags);
      await processDevicesData(devices);
    } catch (err) {
      console.error('Failed to load devices:', err);
      showError('Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
  };

  const loadFilteredDevices = async () => {
    try {
      setLoadingDevices(true);
      const devices = filterDevicesByTier(await edgeDeviceService.getDevices(), visibilityFlags);

      // Filter devices based on selected filters (AND logic between categories)
      const filteredDevices = devices.filter(device => {
        // Get device's location info
        const deviceLocation = locations.find(l => l.id === device.locationId);
        const deviceCountry = deviceLocation ? countries.find(c => c.id === deviceLocation.countryId) : null;
        const deviceRegion = deviceCountry ? regions.find(r => r.id === deviceCountry.regionId) : null;

        // Check region filter (if any regions selected, device must match one)
        const matchesRegion = selectedRegionIds.length === 0 ||
          (deviceRegion && selectedRegionIds.includes(deviceRegion.id));

        // Check country filter
        const matchesCountry = selectedCountryIds.length === 0 ||
          (deviceCountry && selectedCountryIds.includes(deviceCountry.id));

        // Check location filter
        const matchesLocation = selectedLocationIds.length === 0 ||
          (device.locationId && selectedLocationIds.includes(device.locationId));

        return matchesRegion && matchesCountry && matchesLocation;
      });

      await processDevicesData(filteredDevices);
    } catch (err) {
      console.error('Failed to load filtered devices:', err);
      showError('Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
  };

  /**
   * Process devices data - extract modems, aggregate networks
   * Works with both filtered devices list or all devices
   */
  const processDevicesData = async (rawDevices: CentralEdgeDevice[]) => {
    // Always exclude demo devices from analytics — their data isn't real
    const devices = rawDevices.filter(d => {
      const tier = d.deviceTier || (d.isDemo ? 'demo' : 'production');
      return tier !== 'demo';
    });
    setAllDevices(devices);

    // Collect all modems from real devices
    const modems: CentralEdgeModem[] = [];
    for (const device of devices) {
      if (device.modems && Array.isArray(device.modems)) {
        modems.push(
          ...device.modems.map(m => ({
            ...m,
            deviceId: device.id,
            deviceName: device.deviceId
          }))
        );
      }
    }
    setAllModems(modems);

    // Aggregate networks from the central server's available_networks collection
    // This uses the /api/devices/{deviceId}/networks endpoint directly
    const networkMap = new Map<string, AggregatedNetwork & { modemIdSet: Set<string> }>();
    const now = Date.now();

    for (const device of devices) {
      try {
        // Fetch available networks for this device directly from central server
        const networkData = await edgeDeviceService.getDeviceAvailableNetworks(device.id);

        for (const network of networkData.networks) {
          const key = `${network.mcc || ''}-${network.mnc || ''}-${network.operator_name || ''}`;
          const modemKey = network.modemId || device.id; // Track unique modems

          // Parse timestamp
          let timestamp: Date | undefined;
          if (network.timestamp) {
            if (network.timestamp.toDate) {
              timestamp = network.timestamp.toDate();
            } else if (network.timestamp._seconds) {
              timestamp = new Date(network.timestamp._seconds * 1000);
            } else {
              timestamp = new Date(network.timestamp);
            }
          }

          // Calculate if this scan is stale (older than 4 hours)
          const isStale = timestamp ? (now - timestamp.getTime() > STALENESS_THRESHOLD_MS) : true;

          if (!networkMap.has(key)) {
            networkMap.set(key, {
              id: key,
              networkName: network.operator_name || 'Unknown',
              mcc: network.mcc || '',
              mnc: network.mnc || '',
              networkType: network.network_type,
              networkGeneration: network.network_generation,
              modemCount: 1,
              isAvailable: network.is_available !== false && !isStale,
              modemIds: [modemKey],
              modemIdSet: new Set([modemKey]),
              latestTimestamp: timestamp,
              isStale
            });
          } else {
            const existing = networkMap.get(key)!;
            // Only increment if this is a NEW modem detecting this network
            if (!existing.modemIdSet.has(modemKey)) {
              existing.modemIdSet.add(modemKey);
              existing.modemIds = Array.from(existing.modemIdSet);
              existing.modemCount = existing.modemIdSet.size;
            }
            // Update availability if any modem sees it as available AND not stale
            if (network.is_available && !isStale) {
              existing.isAvailable = true;
              existing.isStale = false;
            }
            // Track the latest timestamp
            if (timestamp && (!existing.latestTimestamp || timestamp > existing.latestTimestamp)) {
              existing.latestTimestamp = timestamp;
            }
          }
        }
      } catch (err) {
        console.warn(`[LocationAnalytics] Failed to fetch networks for device ${device.id}:`, err);
        // Continue with other devices even if one fails
      }
    }

    // Convert to array, removing the internal Set
    const networksArray: AggregatedNetwork[] = Array.from(networkMap.values()).map(({ modemIdSet, ...network }) => network);
    setNetworksAtLocation(networksArray);

    // Select all modems by default
    setSelectedModemIds(modems.map(m => m.modemId || m.imei || m.id).filter((id): id is string => !!id));
  };

  const loadLocationCapabilities = async () => {
    // Load capabilities if only one location is selected
    if (selectedLocationIds.length === 1) {
      setLoadingCapabilities(true);
      try {
        const caps = await networkAvailabilityService.getLocationCapabilities(selectedLocationIds[0]);
        setLocationCapabilities(caps);
      } catch (err) {
        console.error('Failed to load capabilities:', err);
      } finally {
        setLoadingCapabilities(false);
      }
    } else {
      setLocationCapabilities(null);
    }
  };

  const handleRefresh = async () => {
    const hasFilters = selectedRegionIds.length > 0 || selectedCountryIds.length > 0 || selectedLocationIds.length > 0;
    if (hasFilters) {
      await loadFilteredDevices();
    } else {
      await loadAllDevices();
    }
    await loadLocationCapabilities();
    success('Data refreshed');
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedRegionIds([]);
    setSelectedCountryIds([]);
    setSelectedLocationIds([]);
  };

  // Check if any filters are active
  const hasActiveFilters = selectedRegionIds.length > 0 || selectedCountryIds.length > 0 || selectedLocationIds.length > 0;

  const handleRecalculateCapabilities = async () => {
    if (selectedLocationIds.length !== 1) return;

    try {
      setLoadingCapabilities(true);
      const caps = await networkAvailabilityService.recalculateLocationCapabilities(selectedLocationIds[0]);
      setLocationCapabilities(caps);
      success('Capabilities recalculated');
    } catch (err) {
      console.error('Failed to recalculate capabilities:', err);
      showError('Failed to recalculate capabilities');
    } finally {
      setLoadingCapabilities(false);
    }
  };

  // Calculate stats for selected modems
  const modemStats = useMemo<ModemStats>(() => {
    const getModemId = (m: CentralEdgeModem) => m.modemId || m.imei || m.id;
    const selectedModems = allModems.filter(m =>
      selectedModemIds.includes(getModemId(m) || '')
    );

    const onlineModems = selectedModems.filter(m => m.status === 'connected');
    const offlineModems = selectedModems.filter(m => m.status === 'disconnected' || m.status === 'error');

    // Calculate average signal
    const signalValues = selectedModems
      .filter(m => m.signalQuality?.rssi !== undefined)
      .map(m => m.signalQuality!.rssi!);
    const avgSignal = signalValues.length > 0
      ? Math.round(signalValues.reduce((a, b) => a + b, 0) / signalValues.length)
      : 0;

    // Calculate signal distribution
    const signalDistribution = [
      { range: '0-20%', count: 0 },
      { range: '21-40%', count: 0 },
      { range: '41-60%', count: 0 },
      { range: '61-80%', count: 0 },
      { range: '81-100%', count: 0 },
    ];

    for (const modem of selectedModems) {
      const signal = modem.signalQuality?.rssi ?? 0;
      if (signal <= 20) signalDistribution[0].count++;
      else if (signal <= 40) signalDistribution[1].count++;
      else if (signal <= 60) signalDistribution[2].count++;
      else if (signal <= 80) signalDistribution[3].count++;
      else signalDistribution[4].count++;
    }

    return {
      totalModems: selectedModems.length,
      onlineModems: onlineModems.length,
      offlineModems: offlineModems.length,
      avgSignal,
      signalDistribution
    };
  }, [allModems, selectedModemIds]);

  // Filter options for multi-select dropdowns
  const regionOptions: MultiSelectOption[] = useMemo(() =>
    regions.map(r => ({ value: r.id, label: r.name })),
    [regions]
  );

  const countryOptions: MultiSelectOption[] = useMemo(() => {
    // Filter countries based on selected regions (if any)
    let filtered = countries;
    if (selectedRegionIds.length > 0) {
      filtered = countries.filter(c => selectedRegionIds.includes(c.regionId));
    }
    return filtered.map(c => ({ value: c.id, label: c.name }));
  }, [countries, selectedRegionIds]);

  const locationOptions: MultiSelectOption[] = useMemo(() => {
    // Filter locations based on selected countries (if any)
    let filtered = locations;
    if (selectedCountryIds.length > 0) {
      filtered = locations.filter(l => selectedCountryIds.includes(l.countryId));
    } else if (selectedRegionIds.length > 0) {
      // Filter by regions if no countries selected
      const countryIdsInRegions = countries
        .filter(c => selectedRegionIds.includes(c.regionId))
        .map(c => c.id);
      filtered = locations.filter(l => countryIdsInRegions.includes(l.countryId));
    }
    return filtered.map(l => ({ value: l.id, label: l.name }));
  }, [locations, countries, selectedCountryIds, selectedRegionIds]);

  // Filter networks to only show those seen by selected modems
  const filteredNetworks = useMemo(() => {
    if (selectedModemIds.length === 0) {
      return []; // No modems selected, show no networks
    }

    // Filter networks to only include those detected by at least one selected modem
    return networksAtLocation.filter(network => {
      // Check if any of the selected modems detected this network
      return network.modemIds.some(modemId => selectedModemIds.includes(modemId));
    }).map(network => {
      // Recalculate modem count based on selected modems only
      const selectedModemCount = network.modemIds.filter(id => selectedModemIds.includes(id)).length;
      return {
        ...network,
        modemCount: selectedModemCount
      };
    });
  }, [networksAtLocation, selectedModemIds]);

  // Helper to get modem ID
  const getModemId = (m: CentralEdgeModem): string => m.modemId || m.imei || m.id;

  // Toggle modem selection
  const toggleModemSelection = (modemId: string) => {
    setSelectedModemIds(prev =>
      prev.includes(modemId)
        ? prev.filter(id => id !== modemId)
        : [...prev, modemId]
    );
  };

  const selectAllModems = () => {
    setSelectedModemIds(allModems.map(m => getModemId(m)));
  };

  const deselectAllModems = () => {
    setSelectedModemIds([]);
  };

  if (!isSystemAdmin) {
    return null;
  }

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2 flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              Location Analytics
            </h1>
            <p className="text-body text-muted-foreground">
              Analyze modem performance, network availability, and capabilities by location
            </p>
          </div>

          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={loadingDevices}
            icon={<RefreshCw className={cn('w-4 h-4', loadingDevices && 'animate-spin')} />}
          >
            Refresh
          </Button>
        </div>

        {/* Multi-Select Filters */}
        <Card padding="lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filter by Location
            </h3>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MultiSelectFilter
              label="Regions"
              options={regionOptions}
              selected={selectedRegionIds}
              onChange={setSelectedRegionIds}
              placeholder="All regions"
            />
            <MultiSelectFilter
              label="Countries"
              options={countryOptions}
              selected={selectedCountryIds}
              onChange={setSelectedCountryIds}
              placeholder="All countries"
            />
            <MultiSelectFilter
              label="Locations"
              options={locationOptions}
              selected={selectedLocationIds}
              onChange={setSelectedLocationIds}
              placeholder="All locations"
            />
          </div>

          {hasActiveFilters && (
            <div className="mt-4 text-sm text-muted-foreground">
              Showing {allDevices.length} devices matching filters
            </div>
          )}
        </Card>

        {/* Modem Selector */}
        {allModems.length > 0 && (
          <Card padding="lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Cpu className="w-5 h-5" />
                Modems ({selectedModemIds.length} of {allModems.length} selected)
              </h3>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={selectAllModems}>
                  Select All
                </Button>
                <Button variant="ghost" size="sm" onClick={deselectAllModems}>
                  Clear
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {allModems.map(modem => {
                const modemId = getModemId(modem);
                const isSelected = selectedModemIds.includes(modemId);
                const isOnline = modem.status === 'online' || modem.status === 'connected';

                return (
                  <button
                    key={modemId}
                    onClick={() => toggleModemSelection(modemId)}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-foreground border-border hover:border-primary'
                    )}
                  >
                    <span className={cn(
                      'w-2 h-2 rounded-full',
                      isOnline ? 'bg-status-success' : 'bg-status-error'
                    )} />
                    <span className="text-sm font-medium">
                      {modem.model || modem.imei?.slice(-6) || 'Unknown'}
                    </span>
                    {modem.signalQuality?.rssi !== undefined && (
                      <span className="text-xs opacity-80">
                        {modem.signalQuality.rssi}%
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-caption text-muted-foreground uppercase mb-2">
                  Total Modems
                </div>
                <div className="text-display font-mono font-bold text-foreground">
                  {modemStats.totalModems}
                </div>
              </div>
              <div className="w-12 h-12 bg-accent-blue/10 rounded flex items-center justify-center">
                <Activity className="w-6 h-6 text-accent-blue" />
              </div>
            </div>
          </Card>

          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-caption text-muted-foreground uppercase mb-2">
                  Online
                </div>
                <div className="text-display font-mono font-bold text-status-success">
                  {modemStats.onlineModems}
                </div>
              </div>
              <div className="w-12 h-12 bg-status-success/10 rounded flex items-center justify-center">
                <Power className="w-6 h-6 text-status-success" />
              </div>
            </div>
          </Card>

          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-caption text-muted-foreground uppercase mb-2">
                  Avg Signal
                </div>
                <div className="text-display font-mono font-bold text-foreground">
                  {modemStats.avgSignal}%
                </div>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                <Signal className="w-6 h-6 text-primary" />
              </div>
            </div>
          </Card>

          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-caption text-muted-foreground uppercase mb-2">
                  Networks
                </div>
                <div className="text-display font-mono font-bold text-accent-purple">
                  {filteredNetworks.length}
                </div>
                {filteredNetworks.some(n => n.isStale) && (
                  <div className="text-xs text-status-warning flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    {filteredNetworks.filter(n => n.isStale).length} stale
                  </div>
                )}
              </div>
              <div className="w-12 h-12 bg-accent-purple/10 rounded flex items-center justify-center">
                <Network className="w-6 h-6 text-accent-purple" />
              </div>
            </div>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card padding="lg">
            <h3 className="text-h3 font-semibold text-foreground mb-4">
              Average Signal Strength
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Signal quality for selected modems
            </p>
            <GaugeChart
              value={modemStats.avgSignal}
              min={0}
              max={100}
              unit="%"
              height={240}
              label="Signal Quality"
            />
          </Card>

          <Card padding="lg">
            <h3 className="text-h3 font-semibold text-foreground mb-4">
              Signal Distribution
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Modems per signal strength range
            </p>
            <BarChart
              data={modemStats.signalDistribution}
              bars={[
                { dataKey: 'count', name: 'Modems', color: 'var(--primary)' }
              ]}
              xAxisKey="range"
              height={240}
            />
          </Card>
        </div>

        {/* Location Capabilities - Only shown when a single location is selected */}
        {selectedLocationIds.length === 1 && (
          <Card padding="lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Wifi className="w-5 h-5" />
                Location Capabilities
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRecalculateCapabilities}
                disabled={loadingCapabilities}
              >
                {loadingCapabilities ? <Spinner size="sm" /> : <RefreshCw className="w-4 h-4" />}
                Recalculate
              </Button>
            </div>

            {loadingCapabilities ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : locationCapabilities ? (
              <div className="space-y-4">
                {/* Technologies */}
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Supported Technologies</div>
                  <div className="flex flex-wrap gap-2">
                    {locationCapabilities.technologies.map((tech: string) => (
                      <Badge
                        key={tech}
                        className={cn(
                          'text-sm',
                          tech === '5G' && 'bg-tech-5g-bg text-tech-5g',
                          tech === '4G' && 'bg-accent-blue/10 text-accent-blue',
                          tech === '3G' && 'bg-status-warning-bg text-status-warning',
                          tech === '2G' && 'bg-status-error-bg text-status-error',
                          tech === 'LTE-M' && 'bg-accent-purple/10 text-accent-purple',
                          tech === 'NB-IoT' && 'bg-accent-green/10 text-accent-green'
                        )}
                      >
                        {tech}
                      </Badge>
                    ))}
                    {locationCapabilities.technologies.length === 0 && (
                      <span className="text-muted-foreground text-sm">No technologies detected</span>
                    )}
                  </div>
                </div>

                {/* Features */}
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Available Features</div>
                  <div className="flex flex-wrap gap-2">
                    {locationCapabilities.features.map((feature: string) => (
                      <Badge key={feature} variant="outline" className="text-sm">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {feature}
                      </Badge>
                    ))}
                    {locationCapabilities.features.length === 0 && (
                      <span className="text-muted-foreground text-sm">No features detected</span>
                    )}
                  </div>
                </div>

                {/* Speed Info */}
                {(locationCapabilities.maxDownloadSpeed || locationCapabilities.maxUploadSpeed) && (
                  <div className="flex gap-8">
                    {locationCapabilities.maxDownloadSpeed && (
                      <div>
                        <div className="text-sm text-muted-foreground">Max Download</div>
                        <div className="text-lg font-semibold text-foreground">
                          {locationCapabilities.maxDownloadSpeed} Mbps
                        </div>
                      </div>
                    )}
                    {locationCapabilities.maxUploadSpeed && (
                      <div>
                        <div className="text-sm text-muted-foreground">Max Upload</div>
                        <div className="text-lg font-semibold text-foreground">
                          {locationCapabilities.maxUploadSpeed} Mbps
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Last Updated */}
                {locationCapabilities.lastUpdatedAt && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Last updated: {formatRelativeTime(new Date(locationCapabilities.lastUpdatedAt))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Wifi className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No capability data available</p>
                <p className="text-sm">Capabilities are aggregated from modems at this location</p>
              </div>
            )}
          </Card>
        )}

        {/* Networks at Location */}
        <Card padding="lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Network className="w-5 h-5" />
              Available Networks ({filteredNetworks.length})
              {filteredNetworks.length !== networksAtLocation.length && (
                <span className="text-sm font-normal text-muted-foreground">
                  of {networksAtLocation.length} total
                </span>
              )}
            </h3>
          </div>

          {selectedModemIds.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Network className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No modems selected</p>
              <p className="text-sm">Select modems above to see their detected networks</p>
            </div>
          ) : filteredNetworks.length > 0 ? (
            <div className="space-y-3">
              {filteredNetworks.map(network => (
                <div
                  key={network.id}
                  className={cn(
                    "flex items-center justify-between p-3 bg-card rounded-lg border",
                    network.isStale ? "border-status-warning/50" : "border-border"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      network.isStale ? "bg-status-warning/10" : "bg-accent-blue/10"
                    )}>
                      <Radio className={cn(
                        "w-5 h-5",
                        network.isStale ? "text-status-warning" : "text-accent-blue"
                      )} />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">
                        {network.networkName}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        MCC: {network.mcc} | MNC: {network.mnc}
                      </div>
                      {network.latestTimestamp && (
                        <div className="text-xs text-muted-foreground/70 flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" />
                          Last seen: {formatRelativeTime(network.latestTimestamp)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {network.networkGeneration && (
                      <Badge
                        className={cn(
                          'text-xs',
                          network.networkGeneration === '5G' && 'bg-tech-5g-bg text-tech-5g',
                          network.networkGeneration === '4G' && 'bg-accent-blue/10 text-accent-blue',
                          network.networkGeneration === '3G' && 'bg-status-warning-bg text-status-warning',
                          network.networkGeneration === '2G' && 'bg-status-error-bg text-status-error'
                        )}
                      >
                        {network.networkGeneration}
                      </Badge>
                    )}
                    <div className="text-right">
                      <div className="text-sm font-medium text-foreground">
                        {network.modemCount} {network.modemCount === 1 ? 'modem' : 'modems'}
                      </div>
                      {network.networkType && (
                        <div className="text-xs text-muted-foreground">
                          {network.networkType}
                        </div>
                      )}
                    </div>
                    {network.isStale ? (
                      <Badge variant="outline" className="flex items-center gap-1 border-status-warning text-status-warning">
                        <Clock className="w-3 h-3" />
                        Stale
                      </Badge>
                    ) : network.isAvailable ? (
                      <Badge variant="secondary" className="flex items-center gap-1 bg-status-success-bg text-status-success">
                        <CheckCircle2 className="w-3 h-3" />
                        Available
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="flex items-center gap-1 bg-muted text-muted-foreground">
                        <AlertCircle className="w-3 h-3" />
                        Unavailable
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Network className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No network data available</p>
              <p className="text-sm">Network information appears when modems perform scans</p>
            </div>
          )}
        </Card>
      </div>
    </AppShell>
  );
}
